/**
 * AGC: Confirm Modal Component - Replaces browser confirm() for Base App compatibility
 * AGC: Works in Base App, Farcaster, and desktop browsers
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useEffect } from 'react'
import { AlertTriangle, X } from 'lucide-react'

export interface ConfirmModalProps {
  isOpen: boolean
  message: string
  onConfirm: () => void
  onCancel: () => void
  title?: string
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'primary' | 'danger'
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  message,
  onConfirm,
  onCancel,
  title = 'Confirm Action',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary'
}) => {
  // AGC: Handle ESC key to cancel
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onCancel])

  if (!isOpen) return null

  // AGC: Determine button colors based on variant
  const confirmButtonClass = confirmVariant === 'danger'
    ? 'bg-gradient-to-r from-red-600 to-rose-600 text-red-100 hover:from-red-500 hover:to-rose-500'
    : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-blue-100 hover:from-blue-500 hover:to-cyan-500'

  // AGC: Split message by newlines for better formatting
  const messageLines = message.split('\n')

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fade-in"
      onClick={(e) => {
        // AGC: Close when clicking backdrop (cancel)
        if (e.target === e.currentTarget) {
          onCancel()
        }
      }}
    >
      <div className="bg-slate-900 border-2 border-yellow-500 rounded-2xl shadow-2xl p-6 max-w-md w-full transform animate-scale-in">
        {/* AGC: Header with icon and title */}
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 text-yellow-400">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-yellow-100 mb-2">
              {title}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="flex-shrink-0 text-slate-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* AGC: Message content */}
        <div className="mb-6">
          <div className="text-sm text-slate-200 whitespace-pre-line">
            {messageLines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < messageLines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* AGC: Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 px-6 rounded-lg transition-all"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 ${confirmButtonClass} font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}

// AGC: Custom animations for modal (reuse from AlertModal if already added)
if (typeof document !== 'undefined') {
  const styleId = 'confirm-modal-styles'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      @keyframes fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes scale-in {
        from {
          opacity: 0;
          transform: scale(0.9);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      
      .animate-fade-in {
        animation: fade-in 0.2s ease-out;
      }
      
      .animate-scale-in {
        animation: scale-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
    `
    document.head.appendChild(style)
  }
}


